import React, { useState, useEffect, useCallback } from 'react';
import { getUserBillingHistoryApi, createRefundRequestApi } from '../api/adminBilling.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';

export default function UserProfileBilling({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundError, setRefundError] = useState(null);
  const [refundSuccess, setRefundSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchBillingHistory = useCallback(async () => {
    setLoading(true);
    const result = await apiHandler(() => getUserBillingHistoryApi(userId));
    if (result.success) {
      setData(unwrapData(result));
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchBillingHistory();
  }, [fetchBillingHistory]);

  const openRefundModal = (tx) => {
    setSelectedTx(tx);
    // Default to full refund amount
    setRefundAmount(tx.amount - (tx.refundedAmount || 0));
    setRefundReason('');
    setRefundError(null);
    setRefundSuccess(null);
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setSelectedTx(null);
  };

  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTx) return;

    setSubmitting(true);
    setRefundError(null);
    setRefundSuccess(null);

    const amountNum = parseFloat(refundAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setRefundError('Please enter a valid refund amount');
      setSubmitting(false);
      return;
    }

    const maxRefundable = selectedTx.amount - (selectedTx.refundedAmount || 0);
    if (amountNum > maxRefundable) {
      setRefundError(`Refund amount cannot exceed $${maxRefundable}`);
      setSubmitting(false);
      return;
    }

    const payload = {
      transactionId: selectedTx.id,
      amount: amountNum,
      reason: refundReason
    };

    const result = await apiHandler(() => createRefundRequestApi(payload));
    if (result.success) {
      setRefundSuccess('Refund request submitted successfully and is pending review.');
      setTimeout(() => {
        closeRefundModal();
        fetchBillingHistory();
      }, 2000);
    } else {
      setRefundError(result.message || 'Failed to submit refund request');
    }
    setSubmitting(false);
  };

  const paymentMethods = data?.paymentMethods || [];
  const transactions = data?.transactions || [];
  const subscription = data?.subscription || {};
  const plan = subscription?.plan || {};

  return (
    <div className="profile-section-card mt-6">
      <div className="profile-section-title flex items-center justify-between border-b pb-3 mb-4">
        <span className="flex items-center gap-2 text-slate-800 font-bold text-sm">
          <i className="fas fa-receipt text-sky-800" />
          Subscription & Financial Ledger
        </span>
        <button
          onClick={fetchBillingHistory}
          disabled={loading}
          className="text-xs text-sky-800 hover:underline flex items-center gap-1 font-semibold"
        >
          <i className={`fas fa-rotate ${loading ? 'fa-spin' : ''}`} /> Sync Live
        </button>
      </div>

      {loading ? (
        <div className="db-skeleton-block h-32 w-full" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Subscription State & Saved Cards */}
          <div className="md:col-span-1 border-r pr-6 flex flex-col gap-4">
            <div>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Stripe Plan</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-bold text-sm text-slate-800">{plan.name || 'Freemium'}</span>
                {plan.price != null && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                    ${parseFloat(plan.price)}/{plan.interval}
                  </span>
                )}
              </div>
            </div>

            <div>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Subscription Status</span>
              <div className="mt-1">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                  subscription.status === 'active' ? 'bg-green-50 text-green-700 border border-green-200' :
                  subscription.status === 'past_due' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  subscription.status === 'canceled' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                  'bg-slate-50 text-slate-600 border border-slate-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    subscription.status === 'active' ? 'bg-green-600' :
                    subscription.status === 'past_due' ? 'bg-amber-600' :
                    subscription.status === 'canceled' ? 'bg-rose-600' : 'bg-slate-400'
                  }`} />
                  {subscription.status || 'No Subscription'}
                </span>
              </div>
            </div>

            <div>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Saved Cards</span>
              {paymentMethods.length === 0 ? (
                <div className="text-xs text-slate-400 mt-1">No payment methods recorded.</div>
              ) : (
                <div className="flex flex-col gap-2 mt-1.5">
                  {paymentMethods.map(pm => (
                    <div key={pm.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-2 bg-slate-50 text-xs text-slate-700">
                      <span className="flex items-center gap-1.5 font-medium">
                        <i className={`fab fa-cc-${pm.cardBrand?.toLowerCase() || 'stripe'} text-slate-500 text-sm`} />
                        •••• {pm.cardLast4}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Exp {pm.expiryMonth}/{pm.expiryYear}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Transactions Ledger */}
          <div className="md:col-span-2 flex flex-col">
            <span className="text-xs font-bold text-slate-700 mb-3 flex items-center gap-1.5">
              <i className="fas fa-list-ul" /> Transaction Log
            </span>
            {transactions.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">No transaction records found for this user.</div>
            ) : (
              <div className="overflow-x-auto border border-slate-100 rounded-lg">
                <table className="w-full text-xs text-slate-700 border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 font-semibold">
                      <th className="p-2.5 text-left">Date</th>
                      <th className="p-2.5 text-left">Amount</th>
                      <th className="p-2.5 text-left">Type</th>
                      <th className="p-2.5 text-left">Status</th>
                      <th className="p-2.5 text-center">Invoice</th>
                      <th className="p-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => {
                      const maxRefundable = tx.amount - (tx.refundedAmount || 0);
                      return (
                        <tr key={tx.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="p-2.5 font-medium text-slate-600">
                            {new Date(tx.createdAt).toLocaleDateString()}
                          </td>
                          <td className="p-2.5 font-bold">
                            ${tx.amount}
                            {tx.refundedAmount > 0 && (
                              <span className="text-[10px] font-medium text-rose-500 block">
                                Refunded: ${tx.refundedAmount}
                              </span>
                            )}
                          </td>
                          <td className="p-2.5 capitalize">{tx.type}</td>
                          <td className="p-2.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              tx.status === 'succeeded' ? 'bg-green-50 text-green-700' :
                              tx.status === 'failed' ? 'bg-rose-50 text-rose-700' :
                              tx.status === 'refunded' ? 'bg-blue-50 text-blue-700' :
                              'bg-slate-50 text-slate-600'
                            }`}>
                              {tx.status}
                            </span>
                          </td>
                          <td className="p-2.5 text-center">
                            {tx.invoicePdfUrl ? (
                              <a href={tx.invoicePdfUrl} target="_blank" rel="noopener noreferrer" className="text-sky-800 hover:text-sky-900">
                                <i className="fas fa-file-pdf text-sm" />
                              </a>
                            ) : '—'}
                          </td>
                          <td className="p-2.5 text-right">
                            {tx.status === 'succeeded' && maxRefundable > 0 ? (
                              <button
                                onClick={() => openRefundModal(tx)}
                                className="bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 px-2 py-1 rounded text-[10px] font-bold transition-all"
                              >
                                Refund
                              </button>
                            ) : (
                              <span className="text-[10px] text-slate-400 font-medium">No actions</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Request Refund Modal */}
      {showRefundModal && selectedTx && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full border border-slate-100 shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-slate-50 border-b p-4 flex items-center justify-between">
              <span className="font-bold text-slate-800 flex items-center gap-2">
                <i className="fas fa-hand-holding-dollar text-rose-600" />
                Request Refund Approval
              </span>
              <button onClick={closeRefundModal} className="text-slate-400 hover:text-slate-600 text-sm">
                <i className="fas fa-times" />
              </button>
            </div>

            <form onSubmit={handleRefundSubmit} className="p-5 flex flex-col gap-4 overflow-y-auto">
              {refundError && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-700 font-medium">
                  {refundError}
                </div>
              )}
              {refundSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-700 font-medium">
                  {refundSuccess}
                </div>
              )}

              <div className="text-xs text-slate-500">
                Original Transaction ID: <strong className="font-mono text-[10px]">{selectedTx.id}</strong><br />
                Paid Amount: <strong>${selectedTx.amount}</strong><br />
                Refunded So Far: <strong>${selectedTx.refundedAmount || 0}</strong>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Refund Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  max={selectedTx.amount - (selectedTx.refundedAmount || 0)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2 text-sm text-slate-800"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Reason for Refund</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Enter internal details or comments for the refund..."
                  className="w-full border border-slate-200 rounded-lg p-2 text-xs text-slate-800 h-20"
                  required
                />
              </div>

              <div className="flex gap-3 justify-end border-t pt-4 mt-2">
                <button
                  type="button"
                  onClick={closeRefundModal}
                  className="px-4 py-2 border rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
